import { BASE_UNISWAP_V3_CONTRACT_ADDRESS } from '../../../config/siteContent';

const ASTROLESCENT_API_URL = 'https://api.astrolescent.com/partner/reddicks/prices';
const TREASURY_VALUE_CACHE_TTL_MS = 5 * 60 * 1000;
const BASE_PAIR_SNAPSHOT_URL = `https://api.dexscreener.com/latest/dex/pairs/base/${BASE_UNISWAP_V3_CONTRACT_ADDRESS}`;
const OCI_ENDOWMENT_POOL_URL =
  'https://ociswap.com/resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp/pools';
const OCI_ENDOWMENT_POOL_API_URL =
  'https://api.ociswap.com/pools?token_address=resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp&cursor=0&limit=10';
const RSWAP_LIQUIDITY_URL = 'https://dex.reddicks.meme/liquidity/add';

const GBOF_RESOURCE_ADDRESS = 'resource_rdx1t40vqvcsljc2dhq9tfnjg98de056me5l676zeyf9694tztacnt2utu';
const WETH_RESOURCE_ADDRESS = 'resource_rdx1th09yvv7tgsrv708ffsgqjjf2mhy84mscmj5jwu4g670fh3e5zgef0';
const WSOL_RESOURCE_ADDRESS = 'resource_rdx1t5ljlq97xfcewcdjxsqld89443fchqg96xv8a8k8gdftdycy9haxpx';
const DCKS_RESOURCE_ADDRESS =
  'resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp';
const XRD_RESOURCE_ADDRESS =
  'resource_rdx1tknxxxxxxxxxradxrdxxxxxxxxx009923554798xxxxxxxxxradxrd';
const VBT_RESOURCE_ADDRESS =
  'resource_rdx1t58j7rp8vwp397x6qsghfvakfp3n5znhyd8kz3gkssuej9j8cgshhz';
const MXRD_RESOURCE_ADDRESS =
  'resource_rdx1th0pjp7s7fesstuj2hcggtxphzw3wc06qx5zrx0c6lsg0xalyt0nn0';
const DFP2_RESOURCE_ADDRESS =
  'resource_rdx1t5ywq4c6nd2lxkemkv4uzt8v7x7smjcguzq5sgafwtasa6luq7fclq';
const WOWO_RESOURCE_ADDRESS =
  'resource_rdx1t4kc5ljyrwlxvg54s6gnctt7nwwgx89h9r2gvrpm369s23yhzyyzlx';
const DELIVER_RESOURCE_ADDRESS =
  'resource_rdx1t466mhd2l2jmmzxr8cg3mkwjqhs7zmjgtder2utnh0ue5msxrhyk3t';
const EARLY_RESOURCE_ADDRESS =
  'resource_rdx1t5xv44c0u99z096q00mv74emwmxwjw26m98lwlzq6ddlpe9f5cuc7s';
const ASTRL_RESOURCE_ADDRESS =
  'resource_rdx1t4tjx4g3qzd98nayqxm7qdpj0a0u8ns6a0jrchq49dyfevgh6u0gj3';

const STATIC_TREASURY_COUNTS = Object.freeze({
  treasuryLockedDcks: 125000000,
  liquidXrd: 1000000,
  liquidVbt: 2000000,
  liquidMxrd: 57150000,
  liquidDfp2: 403848,
  liquidWowo: 20440000,
  liquidDeliver: 30000000,
  liquidEarly: 11000000,
  liquidAstrl: 21149,
  upcomingSol: 160,
});

export const OWNER_TREASURY_SNAPSHOT = Object.freeze({
  dateLabel: 'April 3, 2026',
  totalDcks: 160000000,
  totalLsu: 20000000,
  endowmentSeedDcks: 1200000,
});

export const BASE_LP_FALLBACK_SNAPSHOT = Object.freeze({
  dcksAmount: 14778400,
  cbethAmount: 18.1338,
  liquidityUsd: 83948.43,
  priceUsd: 0.00284,
  pairUrl: `https://dexscreener.com/base/${BASE_UNISWAP_V3_CONTRACT_ADDRESS.toLowerCase()}`,
});

export const OCI_ENDOWMENT_FALLBACK_SNAPSHOT = Object.freeze({
  dcksAmount: 4396127.982874566,
  xrdAmount: 10659247.552636679,
  tvlUsd: 25184.904666596995,
  poolAddress: 'component_rdx1cpwwhuxpe2npedx0axkj4nae8uv5222r0syjtu5fxuaxaj78rf30v9',
  lpTokenAddress: 'resource_rdx1ngffpg3d3le29c9ajtjw0dxt9utjckujfr0nrg48lp05j30yfpvj6r',
  poolUrl: OCI_ENDOWMENT_POOL_URL,
});

export const RSWAP_GBOF_FALLBACK_SNAPSHOT = Object.freeze({
  gbofAmount: 279.635,
  dcksAmount: 420000,
  tvlUsd: 1197.48,
  poolUrl: RSWAP_LIQUIDITY_URL,
});

export const SYMBOL_RESOURCE_MAP = {
  DCKS: DCKS_RESOURCE_ADDRESS,
  XRD: XRD_RESOURCE_ADDRESS,
  LSU: XRD_RESOURCE_ADDRESS,
  GBOF: GBOF_RESOURCE_ADDRESS,
  cbETH: WETH_RESOURCE_ADDRESS,
  ETH: WETH_RESOURCE_ADDRESS,
  SOL: WSOL_RESOURCE_ADDRESS,
};

const parsePositiveNumber = (value, fallback) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const parseCompactNumber = (value, fallback) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return fallback;

  const normalized = value.replace(/,/g, '').trim().toUpperCase();
  const multiplier =
    normalized.endsWith('M') ? 1e6 : normalized.endsWith('K') ? 1e3 : 1;
  const parsed = Number.parseFloat(normalized.replace(/[MK]$/, ''));

  return Number.isFinite(parsed) && parsed >= 0 ? parsed * multiplier : fallback;
};

export const normalizeBasePairSnapshot = (payload) => {
  const pair = payload?.pair || payload?.pairs?.[0];
  if (!pair) {
    return { ...BASE_LP_FALLBACK_SNAPSHOT };
  }

  return {
    dcksAmount: parsePositiveNumber(pair?.liquidity?.base, BASE_LP_FALLBACK_SNAPSHOT.dcksAmount),
    cbethAmount: parsePositiveNumber(pair?.liquidity?.quote, BASE_LP_FALLBACK_SNAPSHOT.cbethAmount),
    liquidityUsd: parsePositiveNumber(pair?.liquidity?.usd, BASE_LP_FALLBACK_SNAPSHOT.liquidityUsd),
    priceUsd: parsePositiveNumber(pair?.priceUsd, BASE_LP_FALLBACK_SNAPSHOT.priceUsd),
    pairUrl: pair?.url || BASE_LP_FALLBACK_SNAPSHOT.pairUrl,
  };
};

export const fetchBasePairSnapshot = async () => {
  const response = await fetch(BASE_PAIR_SNAPSHOT_URL);
  if (!response.ok) {
    throw new Error(`Failed to fetch Base pair snapshot (${response.status})`);
  }

  const payload = await response.json();
  return normalizeBasePairSnapshot(payload);
};

export const normalizeOciEndowmentPoolSnapshot = (payload) => {
  const pool =
    payload?.data?.find((entry) => entry?.slug === 'reddicks-xrd') ||
    payload?.data?.find((entry) => entry?.name === 'REDDICKS/XRD');

  if (!pool) {
    return { ...OCI_ENDOWMENT_FALLBACK_SNAPSHOT };
  }

  return {
    dcksAmount: parsePositiveNumber(pool?.x?.liquidity?.token?.now, OCI_ENDOWMENT_FALLBACK_SNAPSHOT.dcksAmount),
    xrdAmount: parsePositiveNumber(pool?.y?.liquidity?.token?.now, OCI_ENDOWMENT_FALLBACK_SNAPSHOT.xrdAmount),
    tvlUsd: parsePositiveNumber(pool?.total_value_locked?.usd?.now, OCI_ENDOWMENT_FALLBACK_SNAPSHOT.tvlUsd),
    poolAddress: pool?.address || OCI_ENDOWMENT_FALLBACK_SNAPSHOT.poolAddress,
    lpTokenAddress: pool?.lp_token_address || OCI_ENDOWMENT_FALLBACK_SNAPSHOT.lpTokenAddress,
    poolUrl: OCI_ENDOWMENT_POOL_URL,
  };
};

export const fetchOciEndowmentPoolSnapshot = async () => {
  const response = await fetch(OCI_ENDOWMENT_POOL_API_URL);
  if (!response.ok) {
    throw new Error(`Failed to fetch Oci endowment pool (${response.status})`);
  }

  const payload = await response.json();
  return normalizeOciEndowmentPoolSnapshot(payload);
};

export const normalizeRswapGbofSnapshot = (html) => {
  if (typeof html !== 'string') {
    return { ...RSWAP_GBOF_FALLBACK_SNAPSHOT };
  }

  const match = html.match(
    /GBOF<!-- -->\s*\/\s*<!-- -->DCKS<\/span><\/div><\/div><\/td><td[^>]*><p[^>]*>.*?<span>([0-9.,K]+)<!-- -->\s*<!-- -->GBOF<\/span><\/p><p[^>]*>.*?<span>([0-9.,K]+)<!-- -->\s*<!-- -->DCKS<\/span><\/p><\/td><td[^>]*>\$<!-- -->([0-9.,]+)<\/td>/s
  );

  if (!match) {
    return { ...RSWAP_GBOF_FALLBACK_SNAPSHOT };
  }

  return {
    gbofAmount: parseCompactNumber(match[1], RSWAP_GBOF_FALLBACK_SNAPSHOT.gbofAmount),
    dcksAmount: parseCompactNumber(match[2], RSWAP_GBOF_FALLBACK_SNAPSHOT.dcksAmount),
    tvlUsd: parseCompactNumber(match[3], RSWAP_GBOF_FALLBACK_SNAPSHOT.tvlUsd),
    poolUrl: RSWAP_LIQUIDITY_URL,
  };
};

export const fetchRswapGbofPoolSnapshot = async () => {
  const response = await fetch(RSWAP_LIQUIDITY_URL);
  if (!response.ok) {
    throw new Error(`Failed to fetch RSwap liquidity board (${response.status})`);
  }

  const html = await response.text();
  return normalizeRswapGbofSnapshot(html);
};

const getBaseLpDcksAmount = (basePairSnapshot = BASE_LP_FALLBACK_SNAPSHOT) =>
  Number.isFinite(basePairSnapshot?.dcksAmount)
    ? basePairSnapshot.dcksAmount
    : BASE_LP_FALLBACK_SNAPSHOT.dcksAmount;

const getBaseLpCbethAmount = (basePairSnapshot = BASE_LP_FALLBACK_SNAPSHOT) =>
  Number.isFinite(basePairSnapshot?.cbethAmount)
    ? basePairSnapshot.cbethAmount
    : BASE_LP_FALLBACK_SNAPSHOT.cbethAmount;

const getOciEndowmentDcksAmount = (ociPoolSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT) =>
  Number.isFinite(ociPoolSnapshot?.dcksAmount)
    ? ociPoolSnapshot.dcksAmount
    : OCI_ENDOWMENT_FALLBACK_SNAPSHOT.dcksAmount;

const getOciEndowmentXrdAmount = (ociPoolSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT) =>
  Number.isFinite(ociPoolSnapshot?.xrdAmount)
    ? ociPoolSnapshot.xrdAmount
    : OCI_ENDOWMENT_FALLBACK_SNAPSHOT.xrdAmount;

const getRswapGbofDcksAmount = (gbofPoolSnapshot = RSWAP_GBOF_FALLBACK_SNAPSHOT) =>
  Number.isFinite(gbofPoolSnapshot?.dcksAmount)
    ? gbofPoolSnapshot.dcksAmount
    : RSWAP_GBOF_FALLBACK_SNAPSHOT.dcksAmount;

const getRswapGbofPoolValueUsd = (gbofPoolSnapshot = RSWAP_GBOF_FALLBACK_SNAPSHOT) =>
  Number.isFinite(gbofPoolSnapshot?.tvlUsd)
    ? gbofPoolSnapshot.tvlUsd
    : RSWAP_GBOF_FALLBACK_SNAPSHOT.tvlUsd;

export const getDerivedTreasuryDcksLiquidAmount = (
  basePairSnapshot = BASE_LP_FALLBACK_SNAPSHOT,
  ociPoolSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT
) =>
  Math.max(
    0,
    OWNER_TREASURY_SNAPSHOT.totalDcks -
      STATIC_TREASURY_COUNTS.treasuryLockedDcks -
      getBaseLpDcksAmount(basePairSnapshot) -
      getOciEndowmentDcksAmount(ociPoolSnapshot)
  );

export const getOperationalTreasuryDcksAmount = (
  basePairSnapshot = BASE_LP_FALLBACK_SNAPSHOT,
  ociPoolSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT
) =>
  Math.max(
    0,
    getDerivedTreasuryDcksLiquidAmount(basePairSnapshot, ociPoolSnapshot) -
      OWNER_TREASURY_SNAPSHOT.endowmentSeedDcks
  );

export const getUnpairedTreasuryDcksAmount = (
  basePairSnapshot = BASE_LP_FALLBACK_SNAPSHOT,
  ociPoolSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT,
  gbofPoolSnapshot = RSWAP_GBOF_FALLBACK_SNAPSHOT
) =>
  Math.max(
    0,
    getOperationalTreasuryDcksAmount(basePairSnapshot, ociPoolSnapshot) -
      getRswapGbofDcksAmount(gbofPoolSnapshot)
  );

export const getLockedTreasuryDcksAmount = () => STATIC_TREASURY_COUNTS.treasuryLockedDcks;

export const buildTreasuryTotals = (
  basePairSnapshot = BASE_LP_FALLBACK_SNAPSHOT,
  ociPoolSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT
) => ({
  DCKS:
    OWNER_TREASURY_SNAPSHOT.totalDcks -
    getOciEndowmentDcksAmount(ociPoolSnapshot) -
    OWNER_TREASURY_SNAPSHOT.endowmentSeedDcks,
  LOCKED_DCKS: getLockedTreasuryDcksAmount(),
  XRD: STATIC_TREASURY_COUNTS.liquidXrd,
  LSU: OWNER_TREASURY_SNAPSHOT.totalLsu,
  ETH: getBaseLpCbethAmount(basePairSnapshot),
  SOL: STATIC_TREASURY_COUNTS.upcomingSol,
});

const getTreasuryLiquidAssetsForValue = (
  basePairSnapshot = BASE_LP_FALLBACK_SNAPSHOT,
  ociPoolSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT,
  gbofPoolSnapshot = RSWAP_GBOF_FALLBACK_SNAPSHOT
) => [
  {
    numericAmount: getUnpairedTreasuryDcksAmount(
      basePairSnapshot,
      ociPoolSnapshot,
      gbofPoolSnapshot
    ),
    resourceAddress: DCKS_RESOURCE_ADDRESS,
  },
  {
    numericAmount: OWNER_TREASURY_SNAPSHOT.totalLsu,
    resourceAddress: SYMBOL_RESOURCE_MAP.LSU,
  },
  {
    numericAmount: STATIC_TREASURY_COUNTS.liquidXrd,
    resourceAddress: XRD_RESOURCE_ADDRESS,
  },
  {
    numericAmount: STATIC_TREASURY_COUNTS.liquidVbt,
    resourceAddress: VBT_RESOURCE_ADDRESS,
  },
  {
    numericAmount: STATIC_TREASURY_COUNTS.liquidMxrd,
    resourceAddress: MXRD_RESOURCE_ADDRESS,
  },
  {
    numericAmount: STATIC_TREASURY_COUNTS.liquidDfp2,
    resourceAddress: DFP2_RESOURCE_ADDRESS,
  },
  {
    numericAmount: STATIC_TREASURY_COUNTS.liquidWowo,
    resourceAddress: WOWO_RESOURCE_ADDRESS,
  },
  {
    numericAmount: STATIC_TREASURY_COUNTS.liquidDeliver,
    resourceAddress: DELIVER_RESOURCE_ADDRESS,
  },
  {
    numericAmount: STATIC_TREASURY_COUNTS.liquidEarly,
    resourceAddress: EARLY_RESOURCE_ADDRESS,
  },
  {
    numericAmount: STATIC_TREASURY_COUNTS.liquidAstrl,
    resourceAddress: ASTRL_RESOURCE_ADDRESS,
  },
];

let cachedTreasuryValueUSD = null;
let cachedTreasuryValueAtMs = 0;
let inFlightTreasuryValuePromise = null;

export const calculateTreasuryTotalValueUSD = (
  priceMap = {},
  basePairSnapshot = BASE_LP_FALLBACK_SNAPSHOT,
  ociPoolSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT,
  gbofPoolSnapshot = RSWAP_GBOF_FALLBACK_SNAPSHOT
) => {
  let total = 0;

  getTreasuryLiquidAssetsForValue(
    basePairSnapshot,
    ociPoolSnapshot,
    gbofPoolSnapshot
  ).forEach((asset) => {
    if (!asset.resourceAddress || asset.numericAmount <= 0) return;

    const info = priceMap[asset.resourceAddress];
    if (info?.tokenPriceUSD) {
      total += info.tokenPriceUSD * asset.numericAmount;
    }
  });

  const getPrice = (symbol) => priceMap[SYMBOL_RESOURCE_MAP[symbol]]?.tokenPriceUSD || 0;

  total += STATIC_TREASURY_COUNTS.treasuryLockedDcks * getPrice('DCKS');
  total += getRswapGbofPoolValueUsd(gbofPoolSnapshot);
  total +=
    getBaseLpDcksAmount(basePairSnapshot) * getPrice('DCKS') +
    getBaseLpCbethAmount(basePairSnapshot) * getPrice('ETH');
  total += STATIC_TREASURY_COUNTS.upcomingSol * getPrice('SOL');

  return total;
};

export const calculateEndowmentTotalValueUSD = (
  priceMap = {},
  ociPoolSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT
) => {
  const dcksPrice = priceMap[DCKS_RESOURCE_ADDRESS]?.tokenPriceUSD || 0;
  return (
    (Number.isFinite(ociPoolSnapshot?.tvlUsd)
      ? ociPoolSnapshot.tvlUsd
      : OCI_ENDOWMENT_FALLBACK_SNAPSHOT.tvlUsd) +
    OWNER_TREASURY_SNAPSHOT.endowmentSeedDcks * dcksPrice
  );
};

export const buildEndowmentTotals = (ociPoolSnapshot = OCI_ENDOWMENT_FALLBACK_SNAPSHOT) => ({
  DCKS:
    getOciEndowmentDcksAmount(ociPoolSnapshot) +
    OWNER_TREASURY_SNAPSHOT.endowmentSeedDcks,
  XRD: getOciEndowmentXrdAmount(ociPoolSnapshot),
  SEED_DCKS: OWNER_TREASURY_SNAPSHOT.endowmentSeedDcks,
  OCI_POOL_DCKS: getOciEndowmentDcksAmount(ociPoolSnapshot),
  OCI_POOL_XRD: getOciEndowmentXrdAmount(ociPoolSnapshot),
  OCI_POOL_USD: Number.isFinite(ociPoolSnapshot?.tvlUsd)
    ? ociPoolSnapshot.tvlUsd
    : OCI_ENDOWMENT_FALLBACK_SNAPSHOT.tvlUsd,
});

export const roundTreasuryValueUpToTenThousand = (value) => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.ceil(value / 10000) * 10000;
};

export const getTreasuryTotalValueUSD = async ({ forceFresh = false } = {}) => {
  const hasFreshCache =
    cachedTreasuryValueUSD !== null &&
    Date.now() - cachedTreasuryValueAtMs < TREASURY_VALUE_CACHE_TTL_MS;

  if (!forceFresh && hasFreshCache) {
    return cachedTreasuryValueUSD;
  }

  if (!forceFresh && inFlightTreasuryValuePromise) {
    return inFlightTreasuryValuePromise;
  }

  inFlightTreasuryValuePromise = (async () => {
    const [priceMap, basePairSnapshot, ociPoolSnapshot, gbofPoolSnapshot] = await Promise.all([
      (async () => {
        const response = await fetch(ASTROLESCENT_API_URL);
        if (!response.ok) {
          throw new Error(`Status: ${response.status}`);
        }

        return response.json();
      })(),
      fetchBasePairSnapshot().catch(() => BASE_LP_FALLBACK_SNAPSHOT),
      fetchOciEndowmentPoolSnapshot().catch(() => OCI_ENDOWMENT_FALLBACK_SNAPSHOT),
      fetchRswapGbofPoolSnapshot().catch(() => RSWAP_GBOF_FALLBACK_SNAPSHOT),
    ]);

    const total = calculateTreasuryTotalValueUSD(
      priceMap,
      basePairSnapshot,
      ociPoolSnapshot,
      gbofPoolSnapshot
    );

    cachedTreasuryValueUSD = total;
    cachedTreasuryValueAtMs = Date.now();

    return total;
  })();

  try {
    return await inFlightTreasuryValuePromise;
  } catch (error) {
    if (cachedTreasuryValueUSD !== null) {
      return cachedTreasuryValueUSD;
    }

    throw error;
  } finally {
    inFlightTreasuryValuePromise = null;
  }
};

export const getRoundedTreasuryTotalValueUSD = async (options) => {
  const total = await getTreasuryTotalValueUSD(options);
  return roundTreasuryValueUpToTenThousand(total);
};

export { ASTROLESCENT_API_URL };
